import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "col-lg-8 mx-auto" }
const _hoisted_2 = { class: "text-center" }
const _hoisted_3 = { class: "col-lg-10 mx-auto" }
const _hoisted_4 = { class: "skin-page-heading" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "accordion mt-4 mb-5" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = {
  key: 1,
  class: "loading-content text-center"
}

import { ref, reactive, computed, onMounted, watch } from "vue";
import WorkflowNavigation from "@/guiide/components/common/WorkflowNavigation.vue";
import ContactUs from "./common/ContactUs.vue";
import PageNav from "@/guiide/components/common/PageNav.vue";
import BadResponseViewer from "@/shared/components/BadResponseViewer.vue";
import { workflow } from "../ts/workflow/workflow";
import View from "../ts/view";
import FinalSalaryOption from "@/guiide/ts/assets/finalsalary/finalSalaryOption";
import { app } from "../ts/app";
import { useStore } from "vuex";
import { FinalSalary } from "../ts/finalSalary";
import FinalSalaryScheme from "../ts/assets/finalsalary/finalSalaryScheme";
import LoadingSpinner from "@/shared/components/LoadingSpinner.vue";
import { FinalSalaryOptionInfoStatic } from "@/guiide/ts/assets/finalsalary/finalSalaryOptionInfoStatic";
import FinalSalarySchemeOptionsDetailResponse from "@/guiide/ts/response/finalSalarySchemeOptionsDetailResponse";
import FinalSalarySchemeOptionsDetail from "@/guiide/ts/response/finalSalarySchemeOptionsDetail";
import OptionsSummary from "@/guiide/components/dboptions/OptionsSummary.vue";
import ImportantInformation from "@/guiide/components/dboptions/ImportantInformation.vue";
import Modelling from "@/guiide/components/dboptions/Modelling.vue";
import NextSteps from "@/guiide/components/dboptions/NextSteps.vue";
import TaxFreeCashModelSupporter from "@/guiide/ts/assets/finalsalary/taxFreeCashModelSupporter";
import DBOptionsAccordionItem from "@/guiide/components/dboptions/DBOptionsAccordionItem.vue";
import type { NextStepsOptions } from '@/guiide/ts/dbOptions/nextStepsOptions';
import { formatter } from "@/shared/ts/formatter";
import { useFinalSalaryStaticOptions } from "../ts/assets/finalsalary/useFinalSalaryOptionInfoStatic";
import { useUIContent } from "../ts/configuration/useUIContent";


export default /*@__PURE__*/_defineComponent({
  __name: 'DBOptions',
  setup(__props) {

const store = useStore();
const { templateText } = useUIContent();

const nextLink = ref("");
const taxFreeCashModelSupporter = reactive<TaxFreeCashModelSupporter>(new TaxFreeCashModelSupporter());
const selectedOptionName = ref("");
const immediateSpendModel = ref(0);
const selectedNextStepsOption = ref<NextStepsOptions>();
const badResponseViewer = ref(null);
const accordionSteps = { options: 0, important: 1, chart: 2, next: 3, last: 3 };
const openAccordionIndexes = ref<number[]>([0]);
const previouslyOpenedAccordionIndexes = ref<number[]>([0]);
const schemeInfo = ref<FinalSalaryScheme | null>(null);
const finalSalaryAsset = ref<FinalSalary | null>(null);
const retirementDate = ref("");
const selectedOptionStartingPension = ref<number>(0);

const { finalSalaryOptionsStatic } = useFinalSalaryStaticOptions();
const fsOptionsDynamic = ref<FinalSalarySchemeOptionsDetailResponse>(null);

onMounted(async () => {
    const nextWorkflowStep = await workflow.getNextWorkflowStep(View.DBOptions);

    if (!nextWorkflowStep) {
        throw new Error('Failed to get next workflow step');
    }

    nextLink.value = nextWorkflowStep.resource;

    await updateModelValuesFromRetiree();
});

watch(
    () => store.state.retiree,
    async () => {
        await updateModelValuesFromRetiree();
    }
);

watch(
    () => taxFreeCashModelSupporter.activeActual,
    (newValue: number) => {
        if (newValue < immediateSpendModel.value) {
            immediateSpendModel.value = newValue;
        }
    }
);

const canProceedToNextStep = computed<boolean>(() => previouslyOpenedAccordionIndexes.value.includes(accordionSteps.last));

const availablePensionOptions = computed<FinalSalarySchemeOptionsDetail[]>(() => fsOptionsDynamic.value?.options.filter((fsoi: FinalSalarySchemeOptionsDetail) => fsoi.show === true) ?? []);
const showOptionMap = computed<Map<FinalSalaryOption, boolean>>(() => new Map(fsOptionsDynamic.value?.options.map((item: FinalSalarySchemeOptionsDetail) => [item.type, item.show]) ?? []));
const availablePensionOptionsStatic = computed<FinalSalaryOptionInfoStatic[]>(() => finalSalaryOptionsStatic.value.getAllOptions().filter((item: FinalSalaryOptionInfoStatic) => showOptionMap.value.get(item.type)));

const selectedOption = computed<FinalSalaryOption>(() => finalSalaryOptionsStatic.value.optionFromString(selectedOptionName.value));

watch(
    () => selectedOption.value,
    (newValue: FinalSalaryOption) => {
        taxFreeCashModelSupporter.changeActive(newValue);
        if (immediateSpendModel.value > taxFreeCashModelSupporter.activeMax) {
            immediateSpendModel.value = taxFreeCashModelSupporter.activeMax;
        }
    }
);

async function onNextClicked() {
    badResponseViewer.value.clear();

    if (!canProceedToNextStep.value) {
        badResponseViewer.value.update(false, "Please expand all of the above sections before proceeding.");
        return;
    }

    if (selectedNextStepsOption.value != "buildPlan") {
        badResponseViewer.value.update(false, "Please choose the 'I want to build a retirement plan' option in the final section to proceed.");
        return;
    }
    
    const taxFreeCashLumpSumAmount = taxFreeCashModelSupporter.getActual(selectedOption.value);
    if (taxFreeCashLumpSumAmount == null || Number.isNaN(taxFreeCashLumpSumAmount)) {
        throw new Error('Failed to get tax-free lump sum amount');
    }

    const immediateSpendAmount = immediateSpendModel.value;

    const result = await app.updateFinalSalaryOption(
        selectedOption.value, 
        taxFreeCashLumpSumAmount, 
        immediateSpendAmount);

    if (result.successful) {
        await workflow.goToNextWorkflowStep();
    } else {
        badResponseViewer.value.update(false, result.message);
    }
}

async function updateModelValuesFromRetiree() {
    if (!store.state.retiree) {
        return;
    }

    if (fsOptionsDynamic.value) {
        return;
    }

    retirementDate.value = formatter.monthAndYearDate(store.state.retiree.retirementDate);
    schemeInfo.value = store.state.retiree.finalSalaries.schemes[0];
    finalSalaryAsset.value = store.state.retiree.finalSalaries.assets[0];

    const fsOptions: FinalSalarySchemeOptionsDetailResponse = await app.getFinalSalaryOptions(schemeInfo.value.id);

    const taxFreeCashSliderInterval = fsOptions.taxFreeCashSliderInterval;
    taxFreeCashModelSupporter.setOptions(fsOptions.options, FinalSalaryOption.SchemeDefault, taxFreeCashSliderInterval);

    const schemeFinalSalary: FinalSalary = store.state.retiree.finalSalaries.assets?.find((fs: FinalSalary) => fs.option != null);
    if (schemeFinalSalary?.option == FinalSalaryOption.PensionIncreaseExchange) {
        selectedOptionName.value = finalSalaryOptionsStatic.value.pensionIncreaseExchange.name;
    } else if (schemeFinalSalary?.option == FinalSalaryOption.BridgingPension) {
        selectedOptionName.value = finalSalaryOptionsStatic.value.bridgingPension.name;
    } else {
        selectedOptionName.value = finalSalaryOptionsStatic.value.standard.name;
    }

    taxFreeCashModelSupporter.selected = schemeFinalSalary?.taxFreeCashAmount ?? 0;
    taxFreeCashModelSupporter.refresh();

    immediateSpendModel.value = schemeFinalSalary?.taxFreeCashImmediateSpendAmount ?? 0;

    fsOptionsDynamic.value = fsOptions;
}

function toggleAccordionItem(index: number) {
    const isOpen = openAccordionIndexes.value.includes(index);

    const isFirstItem = index === 0;
    const previousStepIsOpen = openAccordionIndexes.value.includes(index - 1);
    const wasPreviouslyOpen = previouslyOpenedAccordionIndexes.value.includes(index - 1);

    if (!isOpen) {
        if (isFirstItem || previousStepIsOpen || wasPreviouslyOpen) {
            openAccordionIndexes.value.push(index);
            previouslyOpenedAccordionIndexes.value.push(index);
        }
    } else {
        openAccordionIndexes.value = openAccordionIndexes.value.filter(i => i !== index);
    }
}

function updateSelectedNextStepsOption(newValue: NextStepsOptions) {
    selectedNextStepsOption.value = newValue;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createVNode(WorkflowNavigation, { workflowStage: "DB Options" }),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h2", _hoisted_4, [
          _cache[7] || (_cache[7] = _createTextVNode(" Your pension")),
          (schemeInfo.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_5, " in the " + _toDisplayString(schemeInfo.value.name), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("p", null, _toDisplayString(_unref(templateText)('dboptions.subtitle.text', { retirementDate: retirementDate.value })), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_6, [
      (fsOptionsDynamic.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createVNode(DBOptionsAccordionItem, {
              position: 1,
              open: openAccordionIndexes.value.includes(accordionSteps.options),
              title: `Your options in the ${schemeInfo.value.name}`,
              isNotLastItem: true,
              onToggleAccordionItem: _cache[0] || (_cache[0] = ($event: any) => (toggleAccordionItem(accordionSteps.options)))
            }, {
              default: _withCtx(() => [
                _createVNode(OptionsSummary, {
                  scheme: schemeInfo.value,
                  optionsDynamic: availablePensionOptions.value,
                  availableOptionsStatic: availablePensionOptionsStatic.value
                }, null, 8, ["scheme", "optionsDynamic", "availableOptionsStatic"])
              ]),
              _: 1
            }, 8, ["open", "title"]),
            _createVNode(DBOptionsAccordionItem, {
              position: 2,
              open: openAccordionIndexes.value.includes(accordionSteps.important),
              title: "Important information to consider",
              isNotLastItem: true,
              onToggleAccordionItem: _cache[1] || (_cache[1] = ($event: any) => (toggleAccordionItem(accordionSteps.important)))
            }, {
              default: _withCtx(() => [
                _createVNode(ImportantInformation, { scheme: schemeInfo.value }, null, 8, ["scheme"])
              ]),
              _: 1
            }, 8, ["open"]),
            _createVNode(DBOptionsAccordionItem, {
              position: 3,
              open: openAccordionIndexes.value.includes(accordionSteps.chart),
              title: _unref(templateText)('dboptions.modelling.title', { schemeName: schemeInfo.value.name }),
              isNotLastItem: true,
              onToggleAccordionItem: _cache[4] || (_cache[4] = ($event: any) => (toggleAccordionItem(accordionSteps.chart)))
            }, {
              default: _withCtx(() => [
                _createVNode(Modelling, {
                  "selected-option-name": selectedOptionName.value,
                  "onUpdate:selectedOptionName": _cache[2] || (_cache[2] = ($event: any) => ((selectedOptionName).value = $event)),
                  availableOptionsStatic: availablePensionOptionsStatic.value,
                  taxFreeCashModelSupporter: taxFreeCashModelSupporter,
                  fsOptionsDynamic: fsOptionsDynamic.value,
                  "avc-pot-amount": finalSalaryAsset.value.avcPotAmount,
                  "selected-option-starting-pension": selectedOptionStartingPension.value,
                  "onUpdate:selectedOptionStartingPension": _cache[3] || (_cache[3] = ($event: any) => ((selectedOptionStartingPension).value = $event))
                }, null, 8, ["selected-option-name", "availableOptionsStatic", "taxFreeCashModelSupporter", "fsOptionsDynamic", "avc-pot-amount", "selected-option-starting-pension"])
              ]),
              _: 1
            }, 8, ["open", "title"]),
            _createVNode(DBOptionsAccordionItem, {
              position: 4,
              open: openAccordionIndexes.value.includes(accordionSteps.next),
              title: "Next steps",
              isNotLastItem: false,
              onToggleAccordionItem: _cache[6] || (_cache[6] = ($event: any) => (toggleAccordionItem(accordionSteps.next)))
            }, {
              default: _withCtx(() => [
                _createVNode(NextSteps, {
                  scheme: schemeInfo.value,
                  taxFreeCashModelSupporter: taxFreeCashModelSupporter,
                  selectedOptionName: selectedOptionName.value,
                  "immediate-spend-model": immediateSpendModel.value,
                  "onUpdate:immediateSpendModel": _cache[5] || (_cache[5] = ($event: any) => ((immediateSpendModel).value = $event)),
                  "selected-option-starting-pension": selectedOptionStartingPension.value,
                  onUpdateSelectedNextStepsOption: updateSelectedNextStepsOption
                }, null, 8, ["scheme", "taxFreeCashModelSupporter", "selectedOptionName", "immediate-spend-model", "selected-option-starting-pension"])
              ]),
              _: 1
            }, 8, ["open"])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(LoadingSpinner),
            _cache[8] || (_cache[8] = _createElementVNode("p", null, [
              _createElementVNode("span", { class: "fs-3" }, "Retrieving your information.")
            ], -1))
          ]))
    ]),
    _createVNode(BadResponseViewer, {
      ref_key: "badResponseViewer",
      ref: badResponseViewer
    }, null, 512),
    _createVNode(ContactUs, { class: "mb-5" }),
    _createVNode(PageNav, {
      "back-link": "requirements",
      onNextClicked: onNextClicked,
      "parent-will-handle-next": true,
      "next-link": "haves"
    })
  ]))
}
}

})